import React from 'react';
import { useOrgSettingsForm } from './config/useOrgSettingsForm';
import { Accordion, AccordionSummary, Box, Button } from '@mui/material';
import { schoolConfigSchema } from './config/schema';
import { FormFieldSchema } from '../../components/FormRenderer/types';
import Icons from '../../components/icons';
import OrgSections from './OrgSections';
import Loader from '../../components/Loader';
import ToastMessage from '../../components/ToastMessage';
import { canAccessField } from '../../utils/roleAccess';
import {
  AccordionHeading,
  FixedHeaderBox,
  FormHeader,
  StyledAccordionDetails
} from './styled';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

const OrgSettingForm = () => {
  const {
    control,
    expandedSection,
    loadingSchoolConfigData,
    snackbar,
    userRole,
    handleAccordionChange,
    handleSubmit,
    setValue,
    handleFileUpload,
    setSnackbar
  } = useOrgSettingsForm();

  const { schoolClassesConfig } = useSelector(
    (state: RootState) => state.schoolConfig
  );

  const gradeOptions = (schoolClassesConfig?.grades || []).map(
    (grade: string) => ({
      label: grade,
      value: grade
    })
  );

  const filteredSchema = React.useMemo(() => {
    return schoolConfigSchema.filter((section) => {
      if (!section.allowedRoles) return true;
      return canAccessField(userRole, section.allowedRoles);
    });
  }, [userRole]);

  if (loadingSchoolConfigData === 'LOADING') {
    return (
      <Box style={{ padding: '20px' }}>
        <FormHeader>Edit Organizational Settings</FormHeader>
        <Loader show showFullScreen={false} />
      </Box>
    );
  }
  return (
    <>
      <form onSubmit={handleSubmit}>
        <FixedHeaderBox>
          <FormHeader>Edit Organizational Settings</FormHeader>
          <Button variant='contained' type='submit' color='primary'>
            Save Settings
          </Button>
        </FixedHeaderBox>
        {filteredSchema.map((section: FormFieldSchema, index: number) => {
          const isSection =
            section.type === 'array' && section.name === 'sections';

          const sectionSchema = isSection
            ? (section as any).children || []
            : [section];

          if (section.type === 'array' && section.name === 'schoolInfo') {
            const gradesParentSchema = sectionSchema.find((item: any) =>
              item.children?.find((child: any) => child.name === 'grades')
            );

            if (gradesParentSchema && gradesParentSchema.children) {
              const gradesChildSchema = gradesParentSchema.children.find(
                (child: any) => child.name === 'grades'
              );
              if (gradesChildSchema) {
                gradesChildSchema.options = gradeOptions;
              }
            }
          }
          return (
            <Box key={section.name} marginTop={index === 0 ? 11 : 1}>
              <Accordion
                expanded={expandedSection === section.name}
                onChange={handleAccordionChange(section.name)}
              >
                <AccordionSummary expandIcon={<Icons.CaretDown />}>
                  <AccordionHeading>{section.label}</AccordionHeading>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <OrgSections
                    userRole={userRole}
                    control={control}
                    sectionName={section.name}
                    sectionSchema={sectionSchema}
                    setValue={setValue}
                    handleFileUpload={handleFileUpload}
                  />
                </StyledAccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </form>
      <ToastMessage
        open={snackbar.show}
        message={snackbar.message}
        onClose={() => setSnackbar((prev) => ({ ...prev, show: false }))}
      />
    </>
  );
};

export default OrgSettingForm;
